import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { WebDavEvent, publish } from "client_legacy/Documents/WebDav/events";
import { move, IMoveResult } from "client/Api/Documents/WebDav/webDavActions";
import {
	ThingsToMove,
	moveNotConfiguredMessage,
	WebDavThingsToMove,
	Destination,
	convertToCrossStoreDestination,
	WebDavDestination
} from "./common";
import {
	MoveResult,
	MoveFailedItem,
	DocumentStoreType
} from "client/Api/Documents/common";
import { EnabledServer as WebDavEnabledServer } from "client/Features/Documents/ItemPicker/WebDavPicker/ItemSelector";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	move as moveCrossStore,
	WebDavCrossStoreItemModel,
	MoveAcrossDataStoresModel
} from "client/Api/Documents/CrossStoreOperations/crossStoreActions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";

class WebDavMoveImplementer {

	public getEnabledServer(
		thingsToMove: ThingsToMove,
	): WebDavEnabledServer {
		const { allowCrossStoreOperations } = documentsConfiguration();
		switch (thingsToMove.storeKind) {
			case DocumentStoreType.SharePoint:
				if (allowCrossStoreOperations) {
					return {
						kind: "all"
					};
				}
				return {
					kind: "none",
					reason: moveNotConfiguredMessage
				};
			case DocumentStoreType.WebDav:
				if (allowCrossStoreOperations) {
					return {
						kind: "all"
					};
				}
				return {
					kind: "single",
					serverId: thingsToMove.source
						.folder
						.server.id,
					reason: moveNotConfiguredMessage
				};
			default:
				throw new UnreachableCaseError(thingsToMove);
		}
	}

	public move(
		thingsToMove: WebDavThingsToMove,
		destination: Destination,
		signal: AbortSignal
	): Promise<MoveResult> {
		const { allowCrossStoreOperations } = documentsConfiguration();

		switch (destination.storeKind) {
			case DocumentStoreType.SharePoint:
				if (allowCrossStoreOperations) {
					return this.moveInTheBackground(
						thingsToMove,
						destination,
						signal
					);
				}

				throw new Error("Does not support moving to SharePoint");
			case DocumentStoreType.WebDav:
				if (
					thingsToMove.source
						.folder
						.server
						.id === destination.target
							.serverId
				) {
					return this.moveOnTheSameServer(
						thingsToMove,
						destination,
						signal
					);

				}

				if (allowCrossStoreOperations) {
					return this.moveInTheBackground(
						thingsToMove,
						destination,
						signal
					);
				}

				throw new Error("Does not support moving between servers");
			default:
				throw new UnreachableCaseError(destination);
		}
	}

	private async moveOnTheSameServer(
		thingsToMove: WebDavThingsToMove,
		destination: WebDavDestination,
		signal: AbortSignal
	): Promise<MoveResult> {
		const { documents, source } = thingsToMove;
		const itemIds: string[] = documents.map((x) => x.id);

		const { serverId, folderId } = destination.target;

		if (folderId === source.folder.id) {
			return { kind: "same-destination" };
		}

		try {
			const moveResult: IMoveResult = await move(
				serverId,
				itemIds,
				folderId,
				signal
			);

			this.announceMovedItems(serverId, moveResult.movedItems);

			const failedItemIds: string[] = moveResult.failedItems;
			if (failedItemIds.length === 0) {
				return { kind: "success" };
			}

			const failedItems: MoveFailedItem[] = documents.filter((item) => failedItemIds.indexOf(item.id) !== -1)
				.map((item) => ({ name: item.name }));

			return { kind: "failed", failedItems };
		} catch (e) {
			if (isCancelledError(e)) {
				throw e;
			}

			return { kind: "failed", failedItems: documents.map((item) => ({ name: item.name }))};
		}
	}

	private async moveInTheBackground(
		thingsToMove: WebDavThingsToMove,
		destination: Destination,
		signal: AbortSignal
	): Promise<MoveResult> {
		const { documents, source } = thingsToMove;
		const itemsToMove: WebDavCrossStoreItemModel[] = documents.map(it => ({
			id: it.id,
			serverId: source.folder.server.id
		}));

		const data: MoveAcrossDataStoresModel = {
			items: {
				storeType: DocumentStoreType.WebDav,
				webDavItems: itemsToMove
			},
			destination: convertToCrossStoreDestination(destination)
		};

		try {
			await moveCrossStore(data, signal);
			return { kind: "scheduled" };

		} catch (e) {
			if (isCancelledError(e)) {
				throw e;
			}

			return { kind: "failed", failedItems: documents.map((x) => ({ name: x.name })) };
		}
	}

	private announceMovedItems(serverId: number, movedItems: { [itemId: string]: FileSystemItemModel; }): void {
		Object.keys(movedItems)
			.forEach((oldItemId) => {
				const movedItem: FileSystemItemModel = movedItems[oldItemId];

				publish(
					WebDavEvent.Moved,
					{
						serverId: serverId,
						oldItemId: oldItemId,
						item: movedItem
					}
				);
			});
	}
}

export {
	WebDavMoveImplementer
};
